import classNames from 'classnames';
import React from 'react';

import {RegistryLabelingStrategy, SequenceSchemaType} from 'constants/RegistryConstants';
import FieldActionButtons from 'metadata/FieldActionButtons';
import DraggableNameTemplate from 'name-templates/DraggableNameTemplate';
import {NameTemplatePart} from 'name-templates/NameTemplateConstants';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {SchemaInterfaceSummary} from 'schemas/schema-interface/SchemaInterfaceConstants';
import SetBySchemaInterfaceTooltip from 'schemas/schema-interface/SetBySchemaInterfaceTooltip';
import getTestProps from 'test/lib/cypress/getTestProps';
import {SettingsLabel} from 'ui-library/SettingsField';

import NameTemplateTypeConfiguration from './NameTemplateTypeConfiguration';

export interface GenericNameTemplateFieldProps {
  bioentityType: string; // see `client/constants/ModelTypeConstants.ts`
  sequenceType: SequenceSchemaType | null; // relevant when bioentityType = SEQUENCE
  schemaFields: Array<SchemaField | ClientSchemaField>;
  schemaId: string;
  nameTemplateParts: Array<NameTemplatePart>;
  isWritable: boolean; // whether we allow changing the name template
  setNameTemplateParts: () => void;
  removeNameTemplateParts: () => void; // required prop, but only used when isDeletable is true
  isDeletable?: boolean; // whether the name template can be deleted, defaults to true
  label?: string;
  helpIconContent?: JSX.Element;
  shouldOrderNamePartsBySequence?: boolean;
  labelingStrategies?: Array<RegistryLabelingStrategy>;
  className?: string;
  warning?: JSX.Element;
  testAttrComponent?: string;
  nameTemplatesOverrideSource?: SchemaInterfaceSummary | null;
  nameTemplateTypeConfiguration?: NameTemplateTypeConfiguration;
}

export default function GenericNameTemplateField(props: GenericNameTemplateFieldProps): JSX.Element {
  const isNameTemplateSet = props.nameTemplateParts.length > 0;
  const isDeletable = props.isDeletable !== false; // defaults to true if not specified

  let actionComponent = null;
  if (props.nameTemplatesOverrideSource) {
    actionComponent = (
      <div className="u-small-padding-right">
        <span>
          {' '}
          <SetBySchemaInterfaceTooltip schemaInterfaceIdAndName={props.nameTemplatesOverrideSource} />
        </span>
      </div>
    );
  } else if (props.isWritable) {
    if (isNameTemplateSet) {
      actionComponent = (
        <FieldActionButtons
          isEditing={false}
          edit={props.setNameTemplateParts}
          remove={isDeletable ? props.removeNameTemplateParts : undefined}
        />
      );
    } else {
      actionComponent = (
        <a
          onClick={props.setNameTemplateParts}
          className="u-small-padding-right"
          {...getTestProps({
            component: props.testAttrComponent ?? 'GenericNameTemplateField',
            element: 'openModal',
          })}
        >
          Set name template
        </a>
      );
    }
  } else if (!isNameTemplateSet) {
    actionComponent = 'None';
  }

  return (
    <div
      className={props.className}
      {...getTestProps({component: props.testAttrComponent ?? 'GenericNameTemplateField', element: 'root'})}
    >
      <div>
        <SettingsLabel
          label={props.label ?? 'Name template'}
          helpIcon={props.helpIconContent}
          dataTestAttrs={{component: props.testAttrComponent ?? 'GenericNameTemplateField', element: 'label'}}
        />
      </div>
      <div
        className={classNames({
          editableField: (props.isWritable && isNameTemplateSet) || props.nameTemplatesOverrideSource,
        })}
      >
        <div>
          <DraggableNameTemplate
            nameTemplateParts={props.nameTemplateParts}
            schemaFields={props.schemaFields}
          />
          {props.warning}
        </div>
        {actionComponent}
      </div>
    </div>
  );
}
